import { Doc, Folder } from '../../../../../types';
import {
  UserKeys,
  getPageOfDocs,
  getPageOfDriveFolders,
  updateStarredDocStatus,
} from '../../../../api/users-api';
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { handleErrors } from '../../../../utils/errors';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { parseDriveFolderId } from './parseDriveFolderId';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import DriveGridToolbar from './drive-grid-toolbar';
import DriveAddShareDialog from './drive-add-share-dialog';
import DriveDeleteDialog from './drive-delete-dialog';
import DriveFolderDialog from './drive-folder-dialog';
import DriveMoveDialog from './drive-move-dialog';
import DriveRenameDialog from './drive-rename-dialog';
import DriveRestoreDialog from './drive-restore-dialog';
import DriveTransferDialog from './drive-transfer-dialog';
import DriveTrashDialog from './drive-trash-dialog';
import DriveLabelsActionsDialog from './drive-labels-actions-dialog';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import DriveFileEditPermissionsDialog from '../../../drive/drive-file-edit-permissions-dialog';
import DriveCopyDialog from './drive-copy-dialog';
import shortcutArrow from '../../../../assets/shortcut-arrow.svg';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import UserAvatar from '../../../../shared/user-avatar';
import DriveAddShortcutDialog from './drive-add-shortcut-dialog';
import { useUserSettings } from '../../../../utils/use-user-settings';
import ChangeLogRequiredOverlay from '../../../grid-overlay/change-log-required-overlay';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import { getVisibilityInfo } from '../../../../utils/drive-visibility-utils';
import DriveDocLabelsCell from '../../../../shared/drive-doc-labels-cell';
import { useDriveLabelBadges } from '../../../../shared/hooks/use-drive-label-badges';
import ScopesAlert from '../../../../shared/scopes-alert';
import QuickReportsDialog from '../../../../shared/quick-reports-dialog';
import { driveQuickReportsConfig } from '../../../../shared/quick-reports-config';

import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Star from '@mui/icons-material/Star';
import StarOutline from '@mui/icons-material/StarOutline';
import Pageview from '@mui/icons-material/Pageview';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import Share from '@mui/icons-material/Share';
import FolderIcon from '@mui/icons-material/Folder';
import FileCopy from '@mui/icons-material/FileCopy';
import ContentCopy from '@mui/icons-material/ContentCopy';
import AddToDrive from '@mui/icons-material/AddToDrive';

interface DriveGridProps {
  userEmail: string;
  selectedFolder: string;
  driveId: string;
  setSelectedFolder: Dispatch<SetStateAction<string>>;
  primaryDomain: string;
  driveRoles: string[];
  allFolders: InfiniteData<Folder[]> | undefined;
  changeLogRoles: string[];
  isLoading: boolean;
  isAllUsers?: boolean;
  showChangeLogOverlay?: boolean;
  onOpenChangeLogDialog?: () => void;
  requiredPermissionName?: string;
}

export default function DriveGrid({
  userEmail,
  selectedFolder,
  driveId,
  setSelectedFolder,
  primaryDomain,
  driveRoles,
  allFolders,
  changeLogRoles,
  isLoading,
  isAllUsers,
  showChangeLogOverlay = false,
  onOpenChangeLogDialog = () => {},
  requiredPermissionName = '',
}: DriveGridProps) {
  const { features } = useUserSettings();
  const { createEvent } = useAnalytics();
  const [starDocSelection, setStarDocSelection] = useState<Partial<Doc>>();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();
  const { setSnackbar } = useStore();

  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [clickedDoc, setClickedDoc] = useState<Doc | null>(null);

  const [openQuickReports, setOpenQuickReports] = useState(false);
  const [openFolderDialog, setOpenFolderDialog] = useState(false);
  const [openTrashDialog, setOpenTrashDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [openMoveDialog, setOpenMoveDialog] = useState(false);
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [openAddShareDialog, setOpenAddShareDialog] = useState(false);
  const [openEditShareDialog, setOpenEditShareDialog] = useState(false);
  const [openAddShortcutDialog, setOpenAddShortcutDialog] = useState(false);
  const [openRemoveLabelsDialog, setOpenRemoveLabelsDialog] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    column: 'name',
    sortDirection: 'ASC',
  });

  // Use shared utility to parse selectedFolder
  const { folderPrefix, hasSuffix, isTrash, isShared, isStarred } =
    parseDriveFolderId(selectedFolder);
  const hasValidFolderPrefix = hasSuffix
    ? folderPrefix !== ''
    : selectedFolder !== '';

  const {
    data: infiniteFolderData,
    isLoading: isLoadingFolders,
    isFetching: isFetchingFolders,
    fetchNextPage: fetchNextPageOfFolders,
    isFetchingNextPage: isFetchingNextPageOfFolders,
    hasNextPage: hasNextPageOfFolders,
  } = useInfiniteQuery({
    queryKey: [
      UserKeys.DriveFolders,
      userEmail,
      selectedFolder,
      paginationModel.column,
      paginationModel.sortDirection,
    ],
    queryFn: ({ pageParam }) => {
      return getPageOfDriveFolders(
        userEmail,
        {
          cursor: pageParam,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 100,
        },
        hasSuffix ? folderPrefix : selectedFolder,
        false,
        isTrash,
        isShared,
        isStarred,
        'name',
        paginationModel.column,
        paginationModel.sortDirection
      );
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.cursor,
    enabled:
      userEmail !== '' &&
      driveId !== undefined &&
      driveId !== '' &&
      selectedFolder !== '' &&
      selectedFolder !== 'shared-drives' &&
      hasValidFolderPrefix,
    meta: {
      errorMessage: `There was an issue loading the user's drive folder data. Please refresh and try again.`,
    },
  });

  const isFinishedGettingAllFolders =
    !!infiniteFolderData && !hasNextPageOfFolders;

  const {
    data: infiniteDocData,
    isLoading: isLoadingDocs,
    isFetching: isFetchingDocs,
    fetchNextPage: fetchNextPageOfDocs,
    isFetchingNextPage: isFetchingNextPageOfDocs,
    hasNextPage: hasNextPageOfDocs,
  } = useInfiniteQuery({
    queryKey: [
      UserKeys.DriveDocuments,
      userEmail,
      selectedFolder,
      paginationModel.column,
      paginationModel.sortDirection,
    ],
    queryFn: ({ pageParam }) => {
      return getPageOfDocs(
        userEmail,
        {
          cursor: pageParam,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 100,
        },
        hasSuffix ? folderPrefix : selectedFolder,
        false,
        isTrash,
        isShared,
        isStarred,
        paginationModel.column,
        paginationModel.sortDirection
      );
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.cursor,
    enabled:
      userEmail !== '' &&
      driveId !== undefined &&
      driveId !== '' &&
      selectedFolder !== '' &&
      selectedFolder !== 'shared-drives' &&
      isFinishedGettingAllFolders,
    meta: {
      errorMessage: `There was an issue loading the user's drive file data. Please refresh and try again.`,
    },
  });

  const { mutate, isPending: isUpdatingStarredStatus } = useMutation({
    mutationFn: (data: Partial<Doc>) =>
      updateStarredDocStatus(
        userEmail,
        data.id || '',
        !data.starred || false,
        changeLogMessage
      ),
    onSuccess: (_data, selectedDoc) => {
      const isFolder =
        selectedDoc.mimeType?.includes('folder') ||
        selectedDoc.shortcutDetails?.targetMimeType?.includes('folder');

      setSnackbar({
        open: true,
        severity: 'success',
        message: isFolder
          ? 'Folder updated successfully!'
          : 'Document updated successfully!',
      });

      setChangeLogMessage('');

      if (isFolder) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveFolders, userEmail, selectedFolder],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, userEmail, selectedFolder],
        });
      }

      if (isFolder) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveFolders, userEmail, `${driveId}.starred`],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, userEmail, `${driveId}.starred`],
        });
      }
      void queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments],
      });
      void queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveFolders],
      });
    },
    onError: (error, selectedDoc) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue updating this ${
          selectedDoc.mimeType?.includes('folder') ? 'folder' : 'document'
        }. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.star_drive_content, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.star_drive_content,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  const isShortcut = (row: Doc) =>
    row.mimeType === 'application/vnd.google-apps.shortcut';
  const isTargetTrashed = (row: Doc) =>
    isShortcut(row) && row.shortcutDetails?.targetTrashed;
  const isTargetInaccessible = (row: Doc) =>
    isShortcut(row) && row.shortcutDetails?.targetAccessible === false;

  const isShortcutVisuallyOrClickDisabled = (row: Doc) => {
    return isTargetTrashed(row) || isTargetInaccessible(row);
  };

  const {
    driveLabelsFeatureEnabled,
    canViewDriveLabels,
    driveLabelsAccessGranted,
    checkingDriveLabelsAccess,
    getLabelBadgeInfo,
  } = useDriveLabelBadges(driveRoles);

  const columns: GridColDef<Doc>[] = [
    {
      field: 'title',
      headerName: 'Name',
      sortable: true,
      filterable: false,
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        const isCurrentRowShortcut = isShortcut(params.row);
        const shortcutVisuallyDisabled = isShortcutVisuallyOrClickDisabled(
          params.row
        );
        const targetTrashed = isTargetTrashed(params.row);
        const targetInaccessible = isTargetInaccessible(params.row);

        let tooltipTitle = params.row.title;
        if (targetTrashed) {
          tooltipTitle = `${params.row.title} no longer exists.`;
        } else if (targetInaccessible) {
          tooltipTitle = `You need permission on ${params.row.title}.`;
        }

        let iconSrc = params.row.iconUrl;
        if (
          isCurrentRowShortcut &&
          params.row.shortcutDetails?.targetMimeType
        ) {
          iconSrc = `https://drive-thirdparty.googleusercontent.com/16/type/${params.row.shortcutDetails.targetMimeType}`;
        }

        const documentIcon = (
          <Box sx={{ position: 'relative', width: 16, height: 16 }}>
            <img
              src={iconSrc}
              style={{ width: 15, height: 15, borderRadius: 2 }}
              alt={params.row.title}
            />
            {isCurrentRowShortcut && (
              <Box
                component="img"
                src={shortcutArrow}
                alt="Shortcut"
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  left: -2,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                }}
              />
            )}
          </Box>
        );

        const avatar = (
          <Tooltip title={tooltipTitle}>
            <Stack
              flexDirection="row"
              alignItems="center"
              gap={1}
              sx={{
                opacity: shortcutVisuallyDisabled ? 0.5 : 1,
              }}
            >
              {documentIcon}
              <Typography variant="body2">{params.row.title}</Typography>
            </Stack>
          </Tooltip>
        );
        return avatar;
      },
    },
    {
      field: 'owner',
      headerName: 'Owner',
      sortable: false,
      filterable: false,
      minWidth: 150,
      display: 'flex',
      renderCell: (params) => {
        if (isTargetTrashed(params.row)) {
          return (
            <Typography
              variant="body2"
              onClick={() => {
                setSelectedDocs([params.row.id]);
                setOpenTrashDialog(true);
              }}
              sx={{
                cursor: 'pointer',
                color: 'info.main',
                fontWeight: 'bold',
              }}
            >
              Remove shortcut
            </Typography>
          );
        }

        const ownerEmail = params.row.ownerEmail
          ? params.row.ownerEmail
          : params.row.owners.length > 0
          ? params.row.owners[0].emailAddress
          : '';

        const avatar = (
          <Tooltip title={ownerEmail}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              {ownerEmail !== '' ? (
                <UserAvatar
                  userEmail={ownerEmail}
                  sx={{ width: 24, height: 24 }}
                />
              ) : null}

              <Typography
                variant="body2"
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {ownerEmail !== '' ? params.row.owner : '-'}
              </Typography>
            </Stack>
          </Tooltip>
        );
        return avatar;
      },
    },
    {
      field: 'modifiedDate',
      headerName: 'Last Modified',
      sortable: true,
      filterable: false,
      minWidth: 115,
      type: 'dateTime',
      valueGetter: (value: number) => (value ? new Date(value) : null),
      renderCell: (params) => {
        if (params.row.modifiedDate === 0 || isTargetTrashed(params.row)) {
          return '';
        }

        const formattedDate = params.value?.toLocaleDateString() || '';
        return (
          <Stack alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body2">{formattedDate}</Typography>
          </Stack>
        );
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      display: 'flex',
      flex: 0.4,
      renderCell: (params) => {
        if (isTargetTrashed(params.row)) {
          return null;
        }

        const avatar = (
          <Stack flexDirection="row" alignItems="center">
            <Tooltip arrow title="Copy">
              <span>
                <IconButton
                  size="small"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await navigator.clipboard.writeText(params.row.id);
                    setSnackbar({
                      open: true,
                      severity: 'info',
                      message: 'ID has been copied to clipboard.',
                    });
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={params.row.id}>
              <Typography variant="body2">{params.row.id}</Typography>
            </Tooltip>
          </Stack>
        );
        return avatar;
      },
    },
    {
      field: 'fileSize',
      headerName: 'File Size',
      sortable: false,
      filterable: false,
      type: 'number',
      display: 'flex',
      renderCell: (params) => {
        if (isTargetTrashed(params.row)) {
          return null;
        }

        const size = params.row.fileSize;
        if (size === 0 || params.row.mimeType.includes('folder')) return '0 B';
        if (size < 1000) return size + ' B';
        if (size < 1000000) return Math.round(size / 1000) + ' KB';
        if (size < 1000000000) return Math.round(size / 1000000) + ' MB';

        return <Typography variant="body2">{size}</Typography>;
      },
    },
    ...(driveLabelsFeatureEnabled &&
    canViewDriveLabels &&
    driveLabelsAccessGranted
      ? [
          {
            field: 'labelTitles',
            headerName: 'Labels',
            sortable: false,
            filterable: false,
            display: 'flex',
            minWidth: 150,
            flex: 0.5,
            valueGetter: (_value, row) => row.labelTitles?.join(', ') ?? '',
            renderCell: (params) => (
              <DriveDocLabelsCell
                doc={params.row}
                getLabelBadgeInfo={getLabelBadgeInfo}
              />
            ),
          } as GridColDef<Doc>,
        ]
      : []),
    ...(selectedFolder !== 'shared-drives' &&
    !selectedFolder.includes('.trash') &&
    !selectedFolder.includes('.shared') &&
    !selectedFolder.includes('.starred')
      ? [
          {
            field: 'visibility',
            headerName: 'Visibility',
            sortable: false,
            filterable: false,
            display: 'flex' as const,
            minWidth: 100,
            renderCell: (params) => {
              if (isTargetTrashed(params.row)) {
                return null;
              }

              // Only show visibility if user is owner or has edit permissions
              const ownerEmail = params.row.ownerEmail
                ? params.row.ownerEmail
                : params.row.owners?.length > 0
                ? params.row.owners[0].emailAddress
                : '';

              const isOwner = ownerEmail === userEmail;
              const canEdit = params.row.canEdit === true;

              // Only show visibility info if user is the owner or can edit
              if (!isOwner && !canEdit) {
                return null;
              }

              const visibilityInfo = getVisibilityInfo(
                params.row,
                primaryDomain
              );

              return (
                <Tooltip title={visibilityInfo.tooltip} arrow>
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    width="100%"
                  >
                    {visibilityInfo.icon}
                  </Stack>
                </Tooltip>
              );
            },
          } as GridColDef<Doc>,
        ]
      : []),
    {
      field: 'actions',
      type: 'actions',
      minWidth: 120,
      headerName: 'Actions',
      getActions: (params: GridRowParams) => {
        const buttonArray = [];

        const ownerEmail = params.row.ownerEmail
          ? params.row.ownerEmail
          : params.row.owners.length > 0
          ? params.row.owners[0].emailAddress
          : '';

        const isSharedDrive = Boolean(
          params.row.driveId && params.row.driveId !== ''
        );

        if (isAbleToSeeUIElement(driveRoles, DriveRoles.CanStarDocs.value)) {
          buttonArray.push(
            <GridActionsCellItem
              label={
                params.row.starred ? 'Remove from Starred' : 'Add to Starred'
              }
              icon={
                isUpdatingStarredStatus && clickedDoc?.id === params.row.id ? (
                  <CircularProgress size={20} />
                ) : (
                  <Tooltip
                    arrow
                    title={
                      params.row.starred
                        ? 'Remove from Starred'
                        : 'Add to Starred'
                    }
                  >
                    {params.row.starred ? <Star /> : <StarOutline />}
                  </Tooltip>
                )
              }
              onClick={() => {
                if (
                  isAbleToSeeUIElement(
                    changeLogRoles,
                    DriveRoles.CanStarDocs.value
                  )
                ) {
                  setStarDocSelection(params.row);
                  setOpenChangeLogDialog(true);
                } else {
                  mutate(params.row);
                  setClickedDoc(params.row);
                }
              }}
              disabled={isShortcutVisuallyOrClickDisabled(params.row)}
            />
          );
        }

        if (
          isAbleToSeeUIElement(driveRoles, DriveRoles.CanViewDocuments.value)
        ) {
          buttonArray.push(
            <GridActionsCellItem
              label={'View'}
              icon={<Pageview />}
              onClick={() => {
                let url = '';

                if (params.row.mimeType.includes('folder')) {
                  url = `https://drive.google.com/drive/folders/${params.row.id}`;
                } else if (
                  params.row.shortcutDetails?.targetMimeType.includes('folder')
                ) {
                  url = `https://drive.google.com/drive/folders/${params.row.shortcutDetails?.targetId}`;
                } else {
                  url = `https://drive.google.com/a/${primaryDomain}/file/d/${params.row.id}/view`;
                }

                window.open(url, '_blank', 'noopener, noreferrer');
              }}
              showInMenu
            />
          );
        }

        if (isAbleToSeeUIElement(driveRoles, DriveRoles.CanRenameDocs.value)) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Rename'}
              icon={<DriveFileRenameOutline />}
              onClick={() => {
                setClickedDoc(params.row);
                setOpenRenameDialog(true);
              }}
              showInMenu
            />
          );
        }

        if (
          isAbleToSeeUIElement(driveRoles, DriveRoles.CanCopyDocs.value) &&
          !params.row.mimeType.includes('folder') &&
          !params.row.shortcutDetails?.targetMimeType.includes('folder')
        ) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Make A Copy'}
              icon={<FileCopy />}
              onClick={() => {
                setClickedDoc(params.row);
                setOpenCopyDialog(true);
              }}
              showInMenu
            />
          );
        }

        if (
          isAbleToSeeUIElement(driveRoles, DriveRoles.CanAddShortcuts.value) &&
          !params.row.mimeType.includes('shortcut') &&
          !params.row.shortcutDetails?.targetMimeType.includes('shortcut')
        ) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Add Shortcut'}
              icon={<AddToDrive />}
              onClick={() => {
                setClickedDoc(params.row);
                setOpenAddShortcutDialog(true);
              }}
              showInMenu
            />
          );
        }

        if (isAbleToSeeUIElement(driveRoles, DriveRoles.CanShareDocs.value)) {
          buttonArray.push(
            <GridActionsCellItem
              label={'Share'}
              icon={<Share />}
              onClick={() => {
                setClickedDoc(params.row);
                setOpenEditShareDialog(true);
              }}
              showInMenu
              disabled={ownerEmail === '' && !isSharedDrive}
            />
          );
        }

        if (isShortcutVisuallyOrClickDisabled(params.row)) {
          return buttonArray.filter((button) => !button.props.showInMenu);
        } else {
          return buttonArray;
        }
      },
    },
  ];

  const finalRows = infiniteFolderData?.pages
    ?.flatMap((p) => p.results)
    .concat(infiniteDocData?.pages?.flatMap((p) => p.results) || []) as Doc[];
  const selectedDocList = useMemo(
    () => finalRows?.filter((doc) => selectedDocs?.includes(doc.id)),
    [finalRows, selectedDocs]
  );

  const CustomChangeLogOverlay = () => (
    <ChangeLogRequiredOverlay
      onOpenDialog={onOpenChangeLogDialog}
      permissionName={requiredPermissionName}
    />
  );

  const GridOverlay = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
      }}
    >
      <CustomChangeLogOverlay />
    </Box>
  );

  const toolbarProps = useMemo(
    () => ({
      driveId,
      selectedFolder,
      selectedDocs: selectedDocList,
      setSelectedFolder: (value: SetStateAction<string>) => {
        setSelectedFolder(value);
      },
      setOpenTransferDialog,
      setOpenFolderDialog,
      setOpenAddShareDialog,
      setOpenMoveDialog,
      setOpenRestoreDialog,
      setOpenDeleteDialog,
      setOpenTrashDialog,
      setOpenRemoveLabelsDialog,
      setOpenQuickReports,
      allFolders,
      driveRoles,
      userEmail,
      features,
    }),
    [
      allFolders,
      driveId,
      driveRoles,
      features,
      selectedDocList,
      selectedFolder,
      setSelectedFolder,
      userEmail,
    ]
  );

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {driveLabelsFeatureEnabled &&
          canViewDriveLabels &&
          !driveLabelsAccessGranted &&
          !checkingDriveLabelsAccess && (
            <Box sx={{ flexShrink: 0 }}>
              <ScopesAlert apiKey="driveLabels" />
            </Box>
          )}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <SharedDataGridClient
            apiRef={apiRef}
            checkboxSelection
            keepNonExistentRowsSelected={false}
            disableColumnMenu
            rows={finalRows || []}
            columns={columns}
            slots={{
              toolbar: useToolbar(DriveGridToolbar, toolbarProps),
              ...(showChangeLogOverlay
                ? { noRowsOverlay: GridOverlay, noColumnsOverlay: GridOverlay }
                : createGridOverlays(
                    userEmail === '' ? 'Search Above' : 'Documents',
                    <FolderIcon />,
                    userEmail === ''
                      ? 'Search for a user to view their drive.'
                      : 'This folder is empty'
                  )),
            }}
            slotProps={{
              loadingOverlay: {
                variant: 'linear-progress',
                noRowsVariant: 'skeleton',
              },
            }}
            onRowClick={(params) => {
              const isTrashStarredOrSharedWithMe =
                selectedFolder === `${driveId}.shared` ||
                selectedFolder === `${driveId}.trash` ||
                selectedFolder === `${driveId}.starred`;

              if (
                params.row.mimeType.includes('folder') &&
                !isTrashStarredOrSharedWithMe
              ) {
                setSelectedFolder(params.row.id);
              } else if (
                params.row.shortcutDetails?.targetMimeType.includes('folder')
              ) {
                const folderId = params.row.shortcutDetails.targetId;
                if (!isShortcutVisuallyOrClickDisabled(params.row)) {
                  setSelectedFolder(folderId);
                }
              }
            }}
            selectedRows={selectedDocs}
            setSelectedRows={setSelectedDocs}
            sortingMode="server"
            onSortModelChange={(newModel) => {
              if (isFetchingNextPageOfDocs || isLoading) return;

              let columnName = 'name';
              if (newModel.length > 0 && newModel[0].field === 'modifiedDate') {
                columnName = 'modifiedTime';
              }

              setPaginationModel({
                column: columnName,
                sortDirection:
                  newModel.length > 0 && newModel[0].sort === 'asc'
                    ? 'ASC'
                    : newModel.length > 0 && newModel[0].sort === 'desc'
                    ? 'DESC'
                    : 'ASC',
              });
            }}
            loading={
              isLoading ||
              isLoadingDocs ||
              isFetchingDocs ||
              isLoadingFolders ||
              isFetchingFolders ||
              isFetchingNextPageOfFolders ||
              isFetchingNextPageOfDocs
            }
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
            onRowsScrollEnd={() => {
              if (!isFetchingNextPageOfFolders && hasNextPageOfFolders) {
                void fetchNextPageOfFolders();
              } else {
                if (
                  !isFetchingNextPageOfDocs &&
                  hasNextPageOfDocs &&
                  isFinishedGettingAllFolders
                ) {
                  void fetchNextPageOfDocs();
                }
              }
            }}
          />
        </Box>
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate(starDocSelection!);
        }}
        mutateLoading={isUpdatingStarredStatus}
      />
      <QuickReportsDialog
        open={openQuickReports}
        handleClose={() => setOpenQuickReports(false)}
        sourceData={[userEmail]}
        sourceType="User"
        dialogTitle="Drive Explorer"
        analyticsEvent={analytics.drive.drive_explorer.quick_report}
        contentDescription={`Generate a one-time report for ${userEmail}`}
        reports={driveQuickReportsConfig}
        changeLogRoles={changeLogRoles}
      />
      {selectedDocs && driveId && openTrashDialog ? (
        <DriveTrashDialog
          open={openTrashDialog}
          handleClose={() => setOpenTrashDialog(false)}
          userEmail={userEmail}
          selectedDocs={selectedDocList}
          driveId={driveId}
          selectedFolder={selectedFolder}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
      {selectedDocs && driveId && openRestoreDialog ? (
        <DriveRestoreDialog
          open={openRestoreDialog}
          handleClose={() => setOpenRestoreDialog(false)}
          userEmail={userEmail}
          selectedDocs={selectedDocList}
          driveId={driveId}
          selectedFolder={selectedFolder}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
      {driveId && clickedDoc && openRenameDialog ? (
        <DriveRenameDialog
          open={openRenameDialog}
          handleClose={() => {
            setOpenRenameDialog(false);
            setClickedDoc(null);
          }}
          userEmail={userEmail}
          clickedDoc={clickedDoc}
          selectedFolder={selectedFolder}
          driveId={driveId}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
      {driveId && clickedDoc && openCopyDialog ? (
        <DriveCopyDialog
          open={openCopyDialog}
          handleClose={() => {
            setOpenCopyDialog(false);
            setClickedDoc(null);
          }}
          userEmail={userEmail}
          clickedDoc={clickedDoc}
          selectedFolder={selectedFolder}
          driveId={driveId}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
      {driveId && clickedDoc && openAddShortcutDialog ? (
        <DriveAddShortcutDialog
          open={openAddShortcutDialog}
          handleClose={() => {
            setOpenAddShortcutDialog(false);
            setClickedDoc(null);
          }}
          userEmail={userEmail}
          clickedDoc={clickedDoc}
          driveId={driveId}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
      {driveId && openFolderDialog ? (
        <DriveFolderDialog
          open={openFolderDialog}
          handleClose={() => {
            setOpenFolderDialog(false);
          }}
          userEmail={userEmail}
          driveId={driveId}
          selectedFolder={selectedFolder}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
      {selectedDocs && driveId && openDeleteDialog ? (
        <DriveDeleteDialog
          open={openDeleteDialog}
          handleClose={() => setOpenDeleteDialog(false)}
          userEmail={userEmail}
          selectedDocs={selectedDocList}
          driveId={driveId}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
      {driveId && openTransferDialog ? (
        <DriveTransferDialog
          open={openTransferDialog}
          handleClose={() => setOpenTransferDialog(false)}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
      {selectedDocs && driveId && openMoveDialog ? (
        <DriveMoveDialog
          open={openMoveDialog}
          handleClose={() => setOpenMoveDialog(false)}
          userEmail={userEmail}
          selectedDocs={selectedDocList}
          setSelectedDocs={setSelectedDocs}
          driveId={driveId}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
          allFolders={allFolders}
        />
      ) : null}
      {selectedDocs && driveId && openAddShareDialog ? (
        <DriveAddShareDialog
          open={openAddShareDialog}
          handleClose={() => setOpenAddShareDialog(false)}
          userEmail={userEmail}
          selectedDocs={selectedDocList}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
      {selectedDocs && driveId && openRemoveLabelsDialog ? (
        <DriveLabelsActionsDialog
          open={openRemoveLabelsDialog}
          handleClose={() => setOpenRemoveLabelsDialog(false)}
          userEmail={userEmail}
          selectedDocs={selectedDocList}
          changeLogRoles={changeLogRoles}
          driveRoles={driveRoles}
          onActionComplete={() => setSelectedDocs([])}
        />
      ) : null}
      {clickedDoc &&
      driveId &&
      !selectedFolder.includes('shared-drive') &&
      openEditShareDialog ? (
        <DriveFileEditPermissionsDialog
          open={openEditShareDialog}
          handleClose={() => setOpenEditShareDialog(false)}
          loggedInUser={userEmail}
          primaryDomain={primaryDomain}
          selectedDoc={clickedDoc}
          setSelectedDoc={setClickedDoc}
          driveRoles={driveRoles}
          changeLogRoles={changeLogRoles}
          isAllUsers={isAllUsers}
        />
      ) : null}
    </>
  );
}
